import type { WikiFoldersAdd } from "@nlkb/api-contract/wiki-folders";
import type {
  ConnectionCapability,
  ConnectionCreateIssue,
  ConnectionCreateRequest,
  ConnectionCredentialIssue,
  ConnectionInventory,
  ConnectionListResponse,
  ConnectionPairingIssue,
  ConnectionRevokeResponse,
  CredentialRevokeResponse,
} from "@nlkb/api-contract/connections";
import type {
  AuditResponse,
  ConflictDetail,
  ConflictSummary,
  FileDiffResponse,
  FileHistoryResponse,
  FolderHistoryResponse,
  SearchResponse,
} from "@nlkb/api-contract/content-reads";
import type {
  AgentProfile,
  AgentProfileUpdateRequest,
  KnowledgeRequestCloseRequest,
  KnowledgeRequestCreateRequest,
  KnowledgeRequestDetail,
  KnowledgeRequestListResponse,
  KnowledgeRequestRecipientListResponse,
  KnowledgeRequestSegment,
  OrganizationOutputProfile,
  OrganizationOutputProfileUpdateRequest,
  ProfileSuggestion,
  ProfileSuggestionCreateRequest,
  ProfileSuggestionListResponse,
  ProfileSuggestionResolveRequest,
} from "@nlkb/api-contract/follow-ups";
import type {
  BillingAccountSummary,
  BillingCheckoutResponse,
  BillingCompleteResponse,
  BillingUsage,
  OperatorPartnerProvisionResponse,
} from "@nlkb/api-contract/billing";
import type {
  InvitationAcceptRequest,
  InvitationAcceptResponse,
  InvitationCreateRequest,
  InvitationInventory,
  InvitationIssue,
  InvitationPreview,
  InvitationRevokeResponse,
  MemberDisableRequest,
  MemberDisableResponse,
  MemberInventory,
  CurrentPrincipal,
  CurrentPrincipalUpdateRequest,
  ContentShare,
  ContentShareCreateRequest,
  OrganizationGroupDetail,
  OrganizationGroupSummary,
  OrganizationSummary,
  PersonalVaultRestoreResponse,
  PrincipalDirectoryEntry,
  PublicShareView,
  ServicePrincipal,
  ServicePrincipalCreateRequest,
  VaultGroupMembership,
  VaultGroupMembershipRevokeResponse,
  VaultMembership,
  VaultMembershipInventory,
  VaultMembershipRevokeResponse,
  VaultSummary,
} from "@nlkb/api-contract/management";
import type {
  OperationRequest,
  OperationResponse,
} from "@nlkb/api-contract/operations";
import type {
  ReleaseArchitecture,
  ReleaseManifestResponse,
  ReleaseOperatingSystem,
} from "@nlkb/api-contract/releases";
import type {
  OrganizationLifecycleState,
  OperatorConnectionMetadata,
  OperatorOrganization,
  OperatorOrganizationProvisionRequest,
  OperatorOrganizationProvisionResponse,
  OperatorPrincipalLookup,
  PlatformRole,
  PlatformRoleGrant,
  PlatformRoleRevokeResponse,
  SearchRebuildRequest,
  SearchRebuildStatusResponse,
} from "@nlkb/api-contract/operator";
import type {
  IngestComposeResponse,
  IngestComposeSettings,
  IngestComposeSettingsUpdateRequest,
  IngestComposeTextRequest,
  IngestExtractResponse,
} from "@nlkb/api-contract/ingest";
import type { VaultSnapshotResponse } from "@nlkb/api-contract/synchronization";
import type {
  KnowledgeTemplateCaptureRequest,
  KnowledgeTemplateCreateRequest,
  KnowledgeTemplateDetail,
  KnowledgeTemplateEntry,
  KnowledgeTemplateEntryCreateRequest,
  KnowledgeTemplateEntryUpdateRequest,
  KnowledgeTemplateFileBody,
  KnowledgeTemplateGrant,
  KnowledgeTemplateSummary,
  KnowledgeTemplateUpdateRequest,
  OrganizationTemplatePolicy,
  OrganizationTemplatePolicyUpdateRequest,
  WikiPresetPublishResponse,
} from "@nlkb/api-contract/templates";
import type {
  AgentSkillCreateRequest,
  AgentSkillDetail,
  AgentSkillEntry,
  AgentSkillEntryCreateRequest,
  AgentSkillEntryUpdateRequest,
  AgentSkillFileBody,
  AgentSkillGrant,
  AgentSkillImportRequest,
  AgentSkillImportUrlRequest,
  AgentSkillSummary,
  AgentSkillUpdateRequest,
} from "@nlkb/api-contract/skills";

import { isDesktopRuntime, submitDesktopOperation } from "./desktop";

export interface ImmutableFileBody {
  body: string;
  revisionId: string;
  contentSha256: string;
}

interface ProtocolErrorBody {
  error?: { code?: string; request_id?: string };
}

export class ApiError extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
    readonly requestId: string | null,
  ) {
    super(code);
    this.name = "ApiError";
  }
}

export type TokenProvider = () => Promise<string | null>;

export function normalizeApiBaseUrl(value: unknown): string {
  if (value === undefined || value === null || value === "") return "";
  if (typeof value !== "string") throw new Error("invalid_api_base_url");
  const trimmed = value.trim();
  if (trimmed.length === 0) return "";
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    throw new Error("invalid_api_base_url");
  }
  const developmentHttp =
    parsed.protocol === "http:" &&
    (parsed.hostname === "127.0.0.1" || parsed.hostname === "localhost");
  if (
    (parsed.protocol !== "https:" && !developmentHttp) ||
    parsed.username !== "" ||
    parsed.password !== "" ||
    (parsed.pathname !== "" && parsed.pathname !== "/") ||
    parsed.search !== "" ||
    parsed.hash !== ""
  ) {
    throw new Error("invalid_api_base_url");
  }
  return parsed.origin;
}

async function readProtocolError(response: Response): Promise<ApiError> {
  let body: ProtocolErrorBody = {};
  try {
    body = (await response.json()) as ProtocolErrorBody;
  } catch {
    // A non-protocol upstream response remains a stable client-side failure.
  }
  return new ApiError(
    body.error?.code ?? `http_${String(response.status)}`,
    response.status,
    body.error?.request_id ?? response.headers.get("x-request-id"),
  );
}

export class ApiClient {
  constructor(
    private readonly tokenProvider: TokenProvider,
    private readonly apiBaseUrl = "",
  ) {}

  private async request(
    path: string,
    init: RequestInit = {},
    options: { readonly allowUnauthenticated?: boolean } = {},
  ): Promise<Response> {
    const token = await this.tokenProvider();
    if (token === null && options.allowUnauthenticated !== true) {
      throw new ApiError("invalid_credentials", 401, null);
    }
    const headers = new Headers(init.headers);
    if (token !== null) headers.set("authorization", `Bearer ${token}`);
    if (!headers.has("accept")) headers.set("accept", "application/json");
    if (
      init.body !== undefined &&
      !headers.has("content-type") &&
      !(init.body instanceof FormData)
    ) {
      headers.set("content-type", "application/json");
    }
    const response = await fetch(`${this.apiBaseUrl}${path}`, {
      ...init,
      headers,
    });
    if (!response.ok) throw await readProtocolError(response);
    return response;
  }

  private async json<T>(
    path: string,
    init: RequestInit = {},
    options: { readonly allowUnauthenticated?: boolean } = {},
  ): Promise<T> {
    return (await (await this.request(path, init, options)).json()) as T;
  }

  private async terminalOperation(
    path: string,
    init: RequestInit,
  ): Promise<OperationResponse> {
    return this.terminalResult(await this.json<OperationResponse>(path, init));
  }

  private terminalResult(result: OperationResponse): OperationResponse {
    const interactiveRecursiveDelete =
      result.status === "rejected" &&
      result.error_code === "folder_not_empty" &&
      result.recursive_confirmation_token !== undefined;
    if (result.status === "rejected" && !interactiveRecursiveDelete) {
      throw new ApiError(result.error_code ?? "operation_rejected", 409, null);
    }
    return result;
  }

  listOrganizations(): Promise<{ organizations: OrganizationSummary[] }> {
    return this.json("/v1/organizations");
  }

  createBillingCheckout(input: {
    plan: "solo" | "teams" | "partner";
    invite_code?: string;
    seats?: number;
    organization_name: string;
    organization_slug?: string;
  }): Promise<BillingCheckoutResponse> {
    return this.json("/v1/billing/checkout", {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  completeBillingCheckout(sessionId: string): Promise<BillingCompleteResponse> {
    return this.json("/v1/billing/complete", {
      method: "POST",
      body: JSON.stringify({ session_id: sessionId }),
    });
  }

  createCreditsCheckout(
    billingAccountId: string,
    organizationId?: string,
  ): Promise<BillingCheckoutResponse> {
    return this.json("/v1/billing/credits/checkout", {
      method: "POST",
      body: JSON.stringify({
        billing_account_id: billingAccountId,
        pack: 100,
        ...(organizationId === undefined
          ? {}
          : { organization_id: organizationId }),
      }),
    });
  }

  createResubscribeCheckout(
    billingAccountId: string,
    organizationId?: string,
  ): Promise<BillingCheckoutResponse> {
    return this.json("/v1/billing/resubscribe", {
      method: "POST",
      body: JSON.stringify({
        billing_account_id: billingAccountId,
        ...(organizationId === undefined
          ? {}
          : { organization_id: organizationId }),
      }),
    });
  }

  createPartnerCheckout(
    billingAccountId: string,
  ): Promise<BillingCheckoutResponse> {
    return this.json("/v1/billing/partner-checkout", {
      method: "POST",
      body: JSON.stringify({ billing_account_id: billingAccountId }),
    });
  }

  billingPortal(
    billingAccountId: string,
    organizationId?: string,
  ): Promise<{ portal_url: string }> {
    return this.json("/v1/billing/portal", {
      method: "POST",
      body: JSON.stringify({
        billing_account_id: billingAccountId,
        ...(organizationId === undefined
          ? {}
          : { organization_id: organizationId }),
      }),
    });
  }

  changeBillingPlan(
    billingAccountId: string,
    plan: "solo" | "teams",
    seats?: number,
    organizationId?: string,
  ): Promise<BillingUsage> {
    return this.json("/v1/billing/plan", {
      method: "POST",
      body: JSON.stringify({
        billing_account_id: billingAccountId,
        plan,
        ...(seats === undefined ? {} : { seats }),
        ...(organizationId === undefined
          ? {}
          : { organization_id: organizationId }),
      }),
    });
  }

  changeBillingSeats(
    billingAccountId: string,
    seats: number,
    organizationId?: string,
  ): Promise<BillingUsage> {
    return this.json("/v1/billing/seats", {
      method: "POST",
      body: JSON.stringify({
        billing_account_id: billingAccountId,
        seats,
        ...(organizationId === undefined
          ? {}
          : { organization_id: organizationId }),
      }),
    });
  }

  billingUsage(billingAccountId: string): Promise<BillingUsage> {
    return this.json(
      `/v1/billing/usage?${new URLSearchParams({ billing_account_id: billingAccountId })}`,
    );
  }

  billingAccounts(): Promise<{ accounts: BillingAccountSummary[] }> {
    return this.json("/v1/billing/accounts");
  }

  createBillingOrganization(input: {
    billing_account_id: string;
    name: string;
    slug: string;
    first_owner_email?: string;
  }): Promise<{
    organization_id: string;
    name: string;
    slug: string;
    first_owner_invitation: InvitationIssue | null;
  }> {
    return this.json("/v1/billing/organizations", {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  provisionPartner(input: {
    name: string;
    slug: string;
    first_owner_email: string;
    max_seats?: number;
    max_organizations?: number;
  }): Promise<OperatorPartnerProvisionResponse> {
    return this.json("/v1/operator/partners", {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  attachOrganizationBilling(
    organizationId: string,
    billingAccountId: string,
  ): Promise<unknown> {
    return this.json(
      `/v1/operator/organizations/${organizationId}/attach-billing`,
      {
        method: "POST",
        body: JSON.stringify({ billing_account_id: billingAccountId }),
      },
    );
  }

  currentPrincipal(): Promise<CurrentPrincipal> {
    return this.json("/v1/me");
  }

  updateCurrentPrincipal(
    input: CurrentPrincipalUpdateRequest,
  ): Promise<CurrentPrincipal> {
    return this.json("/v1/me", {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  }

  desktopInstaller(
    operatingSystem: Extract<ReleaseOperatingSystem, "macos" | "windows">,
    architecture: ReleaseArchitecture,
  ): Promise<ReleaseManifestResponse> {
    const query = new URLSearchParams({
      kind: "desktop-installer",
      operating_system: operatingSystem,
      architecture,
    });
    return this.json(`/v1/releases/manifest?${query}`);
  }

  listVaults(organizationId: string): Promise<{ vaults: VaultSummary[] }> {
    return this.json(`/v1/organizations/${organizationId}/vaults`);
  }

  updateOrganization(
    organizationId: string,
    input: { name?: string; anyone_links_allowed?: boolean },
  ): Promise<OrganizationSummary> {
    return this.json(`/v1/organizations/${organizationId}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  }

  destroyVaultContents(
    vaultId: string,
    action: "clear" | "delete",
    confirmation: "delete",
  ): Promise<{ vault_id: string; action: "clear" | "delete" }> {
    return this.json(`/v1/vaults/${vaultId}/${action}`, {
      method: "POST",
      body: JSON.stringify({ confirmation }),
    });
  }

  updateVault(
    vaultId: string,
    input: { name?: string; local_sync_allowed?: boolean },
  ): Promise<VaultSummary> {
    return this.json(`/v1/vaults/${vaultId}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  }

  vaultMemberships(
    vaultId: string,
  ): Promise<{ memberships: VaultMembershipInventory[] }> {
    return this.json(`/v1/vaults/${vaultId}/members`);
  }

  snapshot(vaultId: string): Promise<VaultSnapshotResponse> {
    return this.json(`/v1/vaults/${vaultId}/snapshot`, { method: "POST" });
  }

  async fileBody(
    fileId: string,
    revisionId: string,
  ): Promise<ImmutableFileBody> {
    const query = new URLSearchParams({ revision: revisionId });
    const response = await this.request(`/v1/files/${fileId}?${query}`, {
      headers: { accept: "text/markdown" },
    });
    return {
      body: await response.text(),
      revisionId: response.headers.get("x-revision-id") ?? revisionId,
      contentSha256: response.headers.get("x-content-sha256") ?? "",
    };
  }

  async addWikiFolders(
    vaultId: string,
    selection: WikiFoldersAdd,
  ): Promise<unknown> {
    if (isDesktopRuntime()) {
      const { addDesktopWikiFolders } = await import("./wiki-folder-vault");
      return addDesktopWikiFolders(this, vaultId, selection);
    }
    return this.json(`/v1/vaults/${vaultId}/wiki-folders`, {
      method: "POST",
      body: JSON.stringify(selection),
    });
  }

  async operation(
    vaultId: string,
    operation: OperationRequest,
  ): Promise<OperationResponse> {
    if (isDesktopRuntime()) {
      return this.terminalResult(
        await submitDesktopOperation(vaultId, operation),
      );
    }
    return this.terminalOperation(`/v1/vaults/${vaultId}/operations`, {
      method: "POST",
      body: JSON.stringify(operation),
    });
  }

  fileHistory(fileId: string): Promise<FileHistoryResponse> {
    return this.json(`/v1/files/${fileId}/revisions?limit=100`);
  }

  folderHistory(folderId: string): Promise<FolderHistoryResponse> {
    return this.json(`/v1/folders/${folderId}/revisions?limit=100`);
  }

  fileDiff(
    fileId: string,
    fromRevisionId: string,
    toRevisionId: string,
  ): Promise<FileDiffResponse> {
    const query = new URLSearchParams({
      from: fromRevisionId,
      to: toRevisionId,
    });
    return this.json(`/v1/files/${fileId}/diff?${query}`);
  }

  search(
    organizationId: string,
    queryText: string,
    vaultIds: readonly string[] = [],
  ): Promise<SearchResponse> {
    const query = new URLSearchParams({
      organization_id: organizationId,
      q: queryText,
      limit: "50",
    });
    for (const vaultId of vaultIds) query.append("vault_id", vaultId);
    return this.json(`/v1/search?${query}`);
  }

  conflicts(
    organizationId: string,
    status: "open" | "resolved" = "open",
  ): Promise<{ conflicts: ConflictSummary[] }> {
    const query = new URLSearchParams({
      organization_id: organizationId,
      status,
    });
    return this.json(`/v1/conflicts?${query}`);
  }

  conflict(conflictId: string): Promise<ConflictDetail> {
    return this.json(`/v1/conflicts/${conflictId}`);
  }

  listRequests(
    organizationId: string,
    segment: KnowledgeRequestSegment = "inbox",
  ): Promise<KnowledgeRequestListResponse> {
    return this.json(
      `/v1/organizations/${organizationId}/requests?${new URLSearchParams({
        segment,
      })}`,
    );
  }

  requestRecipients(
    organizationId: string,
    vaultId: string,
  ): Promise<KnowledgeRequestRecipientListResponse> {
    return this.json(
      `/v1/organizations/${organizationId}/request-recipients?${new URLSearchParams(
        { vault_id: vaultId },
      )}`,
    );
  }

  createRequest(
    organizationId: string,
    input: KnowledgeRequestCreateRequest,
  ): Promise<KnowledgeRequestDetail> {
    return this.json(`/v1/organizations/${organizationId}/requests`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  getRequest(
    organizationId: string,
    requestId: string,
  ): Promise<KnowledgeRequestDetail> {
    return this.json(
      `/v1/organizations/${organizationId}/requests/${requestId}`,
    );
  }

  ackRequest(
    organizationId: string,
    requestId: string,
  ): Promise<KnowledgeRequestDetail> {
    return this.json(
      `/v1/organizations/${organizationId}/requests/${requestId}/ack`,
      { method: "POST" },
    );
  }

  closeRequest(
    organizationId: string,
    requestId: string,
    input: KnowledgeRequestCloseRequest,
  ): Promise<KnowledgeRequestDetail> {
    return this.json(
      `/v1/organizations/${organizationId}/requests/${requestId}/close`,
      { method: "POST", body: JSON.stringify(input) },
    );
  }

  agentProfile(organizationId: string): Promise<AgentProfile> {
    return this.json(`/v1/organizations/${organizationId}/agent-profile`);
  }

  updateAgentProfile(
    organizationId: string,
    input: AgentProfileUpdateRequest,
  ): Promise<AgentProfile> {
    return this.json(`/v1/organizations/${organizationId}/agent-profile`, {
      method: "PUT",
      body: JSON.stringify(input),
    });
  }

  outputProfile(organizationId: string): Promise<OrganizationOutputProfile> {
    return this.json(`/v1/organizations/${organizationId}/output-profile`);
  }

  updateOutputProfile(
    organizationId: string,
    input: OrganizationOutputProfileUpdateRequest,
  ): Promise<OrganizationOutputProfile> {
    return this.json(`/v1/organizations/${organizationId}/output-profile`, {
      method: "PUT",
      body: JSON.stringify(input),
    });
  }

  listSuggestions(
    organizationId: string,
  ): Promise<ProfileSuggestionListResponse> {
    return this.json(`/v1/organizations/${organizationId}/suggestions`);
  }

  createSuggestion(
    organizationId: string,
    input: ProfileSuggestionCreateRequest,
  ): Promise<ProfileSuggestion> {
    return this.json(`/v1/organizations/${organizationId}/suggestions`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  resolveSuggestion(
    organizationId: string,
    suggestionId: string,
    input: ProfileSuggestionResolveRequest,
  ): Promise<ProfileSuggestion> {
    return this.json(
      `/v1/organizations/${organizationId}/suggestions/${suggestionId}/resolve`,
      { method: "POST", body: JSON.stringify(input) },
    );
  }

  connections(organizationId: string): Promise<ConnectionListResponse> {
    return this.json(
      `/v1/connections?${new URLSearchParams({ organization_id: organizationId })}`,
    );
  }

  members(organizationId: string): Promise<{ members: MemberInventory[] }> {
    return this.json(`/v1/organizations/${organizationId}/members`);
  }

  principals(
    organizationId: string,
  ): Promise<{ principals: PrincipalDirectoryEntry[] }> {
    return this.json(`/v1/organizations/${organizationId}/principals`);
  }

  createInvitation(
    organizationId: string,
    input: InvitationCreateRequest,
  ): Promise<InvitationIssue> {
    return this.json(`/v1/organizations/${organizationId}/invitations`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  previewInvitation(token: string): Promise<InvitationPreview> {
    return this.json(
      `/v1/invitations/preview?${new URLSearchParams({ token })}`,
    );
  }

  listInvitations(
    organizationId: string,
  ): Promise<{ invitations: InvitationInventory[] }> {
    return this.json(`/v1/organizations/${organizationId}/invitations`);
  }

  revokeInvitation(
    organizationId: string,
    invitationId: string,
  ): Promise<InvitationRevokeResponse> {
    return this.json(
      `/v1/organizations/${organizationId}/invitations/${invitationId}/revoke`,
      { method: "POST" },
    );
  }

  listGroups(
    organizationId: string,
  ): Promise<{ groups: OrganizationGroupSummary[] }> {
    return this.json(`/v1/organizations/${organizationId}/groups`);
  }

  getGroup(
    organizationId: string,
    groupId: string,
  ): Promise<OrganizationGroupDetail> {
    return this.json(`/v1/organizations/${organizationId}/groups/${groupId}`);
  }

  createGroup(
    organizationId: string,
    name: string,
  ): Promise<OrganizationGroupDetail> {
    return this.json(`/v1/organizations/${organizationId}/groups`, {
      method: "POST",
      body: JSON.stringify({ name }),
    });
  }

  updateGroup(
    organizationId: string,
    groupId: string,
    name: string,
  ): Promise<OrganizationGroupDetail> {
    return this.json(`/v1/organizations/${organizationId}/groups/${groupId}`, {
      method: "PATCH",
      body: JSON.stringify({ name }),
    });
  }

  archiveGroup(
    organizationId: string,
    groupId: string,
  ): Promise<{ group_id: string; state: "archived" }> {
    return this.json(
      `/v1/organizations/${organizationId}/groups/${groupId}/archive`,
      { method: "POST" },
    );
  }

  addGroupMember(
    organizationId: string,
    groupId: string,
    principalId: string,
  ): Promise<OrganizationGroupDetail> {
    return this.json(
      `/v1/organizations/${organizationId}/groups/${groupId}/members/${principalId}`,
      { method: "PUT" },
    );
  }

  removeGroupMember(
    organizationId: string,
    groupId: string,
    principalId: string,
  ): Promise<OrganizationGroupDetail> {
    return this.json(
      `/v1/organizations/${organizationId}/groups/${groupId}/members/${principalId}`,
      { method: "DELETE" },
    );
  }

  vaultGroupMemberships(
    vaultId: string,
  ): Promise<{ memberships: VaultGroupMembership[] }> {
    return this.json(`/v1/vaults/${vaultId}/groups`);
  }

  setVaultGroupMembership(
    vaultId: string,
    groupId: string,
    role: "viewer" | "editor" | "vault_admin",
  ): Promise<{ vault_id: string; group_id: string; role: string }> {
    return this.json(`/v1/vaults/${vaultId}/groups/${groupId}`, {
      method: "PUT",
      body: JSON.stringify({ role }),
    });
  }

  revokeVaultGroupMembership(
    vaultId: string,
    groupId: string,
  ): Promise<VaultGroupMembershipRevokeResponse> {
    return this.json(`/v1/vaults/${vaultId}/groups/${groupId}`, {
      method: "DELETE",
    });
  }

  acceptInvitation(
    input: InvitationAcceptRequest,
  ): Promise<InvitationAcceptResponse> {
    return this.json("/v1/invitations/accept", {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  updateMemberRole(
    organizationId: string,
    principalId: string,
    role: "org_owner" | "org_admin" | "member",
  ): Promise<MemberInventory> {
    return this.json(
      `/v1/organizations/${organizationId}/members/${principalId}`,
      { method: "PATCH", body: JSON.stringify({ role }) },
    );
  }

  updateMemberDisplayName(
    organizationId: string,
    principalId: string,
    displayName: string,
  ): Promise<MemberInventory> {
    return this.json(
      `/v1/organizations/${organizationId}/members/${principalId}`,
      {
        method: "PATCH",
        body: JSON.stringify({ display_name: displayName }),
      },
    );
  }

  disableMember(
    organizationId: string,
    principalId: string,
    input: MemberDisableRequest,
  ): Promise<MemberDisableResponse> {
    return this.json(
      `/v1/organizations/${organizationId}/members/${principalId}/disable`,
      { method: "POST", body: JSON.stringify(input) },
    );
  }

  createServicePrincipal(
    organizationId: string,
    input: ServicePrincipalCreateRequest,
  ): Promise<ServicePrincipal> {
    return this.json(`/v1/organizations/${organizationId}/services`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  updateServicePrincipal(
    organizationId: string,
    principalId: string,
    input: { display_name?: string; description?: string | null },
  ): Promise<ServicePrincipal> {
    return this.json(
      `/v1/organizations/${organizationId}/services/${principalId}`,
      { method: "PATCH", body: JSON.stringify(input) },
    );
  }

  createTeamVault(organizationId: string, name: string): Promise<VaultSummary> {
    return this.json(`/v1/organizations/${organizationId}/vaults`, {
      method: "POST",
      body: JSON.stringify({ name }),
    });
  }

  listTemplates(
    organizationId: string,
  ): Promise<{ templates: KnowledgeTemplateSummary[] }> {
    return this.json(`/v1/organizations/${organizationId}/templates`);
  }

  createOrganizationTemplate(
    organizationId: string,
    input: KnowledgeTemplateCreateRequest,
  ): Promise<KnowledgeTemplateSummary> {
    return this.json(`/v1/organizations/${organizationId}/templates`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  templatePolicy(organizationId: string): Promise<OrganizationTemplatePolicy> {
    return this.json(`/v1/organizations/${organizationId}/template-policy`);
  }

  updateTemplatePolicy(
    organizationId: string,
    input: OrganizationTemplatePolicyUpdateRequest,
  ): Promise<OrganizationTemplatePolicy> {
    return this.json(`/v1/organizations/${organizationId}/template-policy`, {
      method: "PUT",
      body: JSON.stringify(input),
    });
  }

  captureVaultTemplate(
    vaultId: string,
    input: KnowledgeTemplateCaptureRequest,
  ): Promise<KnowledgeTemplateSummary> {
    return this.json(`/v1/vaults/${vaultId}/templates`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  getTemplate(
    templateId: string,
    organizationId?: string,
  ): Promise<KnowledgeTemplateDetail> {
    const query =
      organizationId === undefined
        ? ""
        : `?${new URLSearchParams({ organization_id: organizationId })}`;
    return this.json(`/v1/templates/${templateId}${query}`);
  }

  updateTemplate(
    templateId: string,
    input: KnowledgeTemplateUpdateRequest,
  ): Promise<KnowledgeTemplateSummary> {
    return this.json(`/v1/templates/${templateId}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  }

  archiveTemplate(templateId: string): Promise<KnowledgeTemplateSummary> {
    return this.json(`/v1/templates/${templateId}`, { method: "DELETE" });
  }

  promoteTemplate(templateId: string): Promise<KnowledgeTemplateSummary> {
    return this.json(`/v1/templates/${templateId}/promote`, { method: "POST" });
  }

  templateFileBody(
    templateId: string,
    entryId: string,
    organizationId?: string,
  ): Promise<KnowledgeTemplateFileBody> {
    const query =
      organizationId === undefined
        ? ""
        : `?${new URLSearchParams({ organization_id: organizationId })}`;
    return this.json(`/v1/templates/${templateId}/files/${entryId}${query}`);
  }

  createTemplateEntry(
    templateId: string,
    input: KnowledgeTemplateEntryCreateRequest,
  ): Promise<KnowledgeTemplateEntry> {
    return this.json(`/v1/templates/${templateId}/entries`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  updateTemplateEntry(
    templateId: string,
    entryId: string,
    input: KnowledgeTemplateEntryUpdateRequest,
  ): Promise<KnowledgeTemplateEntry> {
    return this.json(`/v1/templates/${templateId}/entries/${entryId}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  }

  deleteTemplateEntry(templateId: string, entryId: string): Promise<void> {
    return this.json(`/v1/templates/${templateId}/entries/${entryId}`, {
      method: "DELETE",
    });
  }

  listPlatformTemplates(): Promise<{ templates: KnowledgeTemplateSummary[] }> {
    return this.json("/v1/operator/templates");
  }

  createPlatformTemplate(
    input: KnowledgeTemplateCreateRequest,
  ): Promise<KnowledgeTemplateSummary> {
    return this.json("/v1/operator/templates", {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  publishWikiPresets(): Promise<WikiPresetPublishResponse> {
    return this.json("/v1/operator/wiki-presets/publish", { method: "POST" });
  }

  async exportTemplateRelatedSkills(
    templateId: string,
    organizationId?: string,
  ): Promise<Blob> {
    const query =
      organizationId === undefined
        ? ""
        : `?${new URLSearchParams({ organization_id: organizationId })}`;
    const response = await this.request(
      `/v1/templates/${templateId}/related-skills/export${query}`,
      { headers: { accept: "application/zip" } },
    );
    return response.blob();
  }

  listTemplateGrants(
    templateId: string,
  ): Promise<{ grants: KnowledgeTemplateGrant[] }> {
    return this.json(`/v1/operator/templates/${templateId}/grants`);
  }

  grantPlatformTemplate(
    templateId: string,
    organizationId: string,
  ): Promise<KnowledgeTemplateGrant> {
    return this.json(`/v1/operator/templates/${templateId}/grants`, {
      method: "POST",
      body: JSON.stringify({ organization_id: organizationId }),
    });
  }

  revokePlatformTemplateGrant(
    templateId: string,
    organizationId: string,
  ): Promise<void> {
    return this.json(
      `/v1/operator/templates/${templateId}/grants/${organizationId}`,
      { method: "DELETE" },
    );
  }

  updateOperatorTemplatePolicy(
    organizationId: string,
    input: OrganizationTemplatePolicyUpdateRequest,
  ): Promise<OrganizationTemplatePolicy> {
    return this.json(
      `/v1/operator/organizations/${organizationId}/template-policy`,
      {
        method: "PUT",
        body: JSON.stringify(input),
      },
    );
  }

  setTemplateLibrary(
    organizationId: string,
    templateLibrary: boolean,
  ): Promise<OrganizationTemplatePolicy> {
    return this.json(
      `/v1/operator/organizations/${organizationId}/template-library`,
      {
        method: "PATCH",
        body: JSON.stringify({ template_library: templateLibrary }),
      },
    );
  }

  setVaultMembership(
    vaultId: string,
    principalId: string,
    role: "viewer" | "editor" | "vault_admin",
  ): Promise<VaultMembership> {
    return this.json(`/v1/vaults/${vaultId}/members/${principalId}`, {
      method: "PUT",
      body: JSON.stringify({ role }),
    });
  }

  revokeVaultMembership(
    vaultId: string,
    principalId: string,
  ): Promise<VaultMembershipRevokeResponse> {
    return this.json(`/v1/vaults/${vaultId}/members/${principalId}`, {
      method: "DELETE",
    });
  }

  restorePersonalVault(vaultId: string): Promise<PersonalVaultRestoreResponse> {
    return this.json(`/v1/vaults/${vaultId}/restore`, { method: "POST" });
  }

  createShare(
    vaultId: string,
    input: ContentShareCreateRequest,
  ): Promise<ContentShare> {
    return this.json(`/v1/vaults/${vaultId}/shares`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  listVaultShares(vaultId: string): Promise<{ shares: ContentShare[] }> {
    return this.json(`/v1/vaults/${vaultId}/shares`);
  }

  listOrganizationShares(
    organizationId: string,
  ): Promise<{ shares: ContentShare[] }> {
    return this.json(`/v1/organizations/${organizationId}/shares`);
  }

  revokeShare(
    shareId: string,
  ): Promise<{ share_id: string; state: "revoked" }> {
    return this.json(`/v1/shares/${shareId}/revoke`, { method: "POST" });
  }

  rotateShare(shareId: string): Promise<ContentShare> {
    return this.json(`/v1/shares/${shareId}/rotate`, { method: "POST" });
  }

  publicShareView(token: string): Promise<PublicShareView> {
    return this.json(
      `/v1/public-shares/${token}`,
      {},
      {
        allowUnauthenticated: true,
      },
    );
  }

  async publicShareFileBody(
    token: string,
    fileId: string,
    revisionId: string,
  ): Promise<ImmutableFileBody> {
    const query = new URLSearchParams({ revision: revisionId });
    const response = await this.request(
      `/v1/public-shares/${token}/files/${fileId}?${query}`,
      { headers: { accept: "text/markdown" } },
      { allowUnauthenticated: true },
    );
    return {
      body: await response.text(),
      revisionId: response.headers.get("x-revision-id") ?? revisionId,
      contentSha256: response.headers.get("x-content-sha256") ?? "",
    };
  }

  audit(
    organizationId: string,
    options: {
      readonly after?: number;
      readonly limit?: number;
      readonly vaultId?: string | null;
    } = {},
  ): Promise<AuditResponse> {
    const query = new URLSearchParams({
      organization_id: organizationId,
      limit: String(options.limit ?? 200),
    });
    if (options.after !== undefined) {
      query.set("after", String(options.after));
    }
    if (options.vaultId !== undefined && options.vaultId !== null) {
      query.set("vault_id", options.vaultId);
    }
    return this.json(`/v1/audit?${query}`);
  }

  async exportCurrent(
    organizationId: string,
    vaultIds: readonly string[],
    entryId?: string,
  ): Promise<Blob> {
    const response = await this.request("/v1/exports", {
      method: "POST",
      body: JSON.stringify({
        organization_id: organizationId,
        vault_ids: vaultIds,
        ...(entryId === undefined ? {} : { entry_id: entryId }),
      }),
      headers: { accept: "application/zip" },
    });
    return response.blob();
  }

  operatorOrganizations(): Promise<{ organizations: OperatorOrganization[] }> {
    return this.json("/v1/operator/organizations");
  }

  provisionOperatorOrganization(
    input: OperatorOrganizationProvisionRequest,
  ): Promise<OperatorOrganizationProvisionResponse> {
    return this.json("/v1/operator/organizations", {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  updateOperatorOrganizationLifecycle(
    organizationId: string,
    lifecycleState: OrganizationLifecycleState,
  ): Promise<OperatorOrganization> {
    return this.json(`/v1/operator/organizations/${organizationId}/lifecycle`, {
      method: "PATCH",
      body: JSON.stringify({ lifecycle_state: lifecycleState }),
    });
  }

  operatorConnections(
    organizationId: string,
  ): Promise<{ connections: OperatorConnectionMetadata[] }> {
    return this.json(
      `/v1/operator/organizations/${organizationId}/connections`,
    );
  }

  operatorPrincipalsByEmail(
    email: string,
  ): Promise<{ principals: OperatorPrincipalLookup[] }> {
    return this.json(
      `/v1/operator/principals?${new URLSearchParams({ email })}`,
    );
  }

  platformRoles(): Promise<{ grants: PlatformRoleGrant[] }> {
    return this.json("/v1/operator/platform-roles");
  }

  grantPlatformRole(
    principalId: string,
    role: PlatformRole,
  ): Promise<PlatformRoleGrant> {
    return this.json(`/v1/operator/platform-roles/${principalId}/${role}`, {
      method: "PUT",
    });
  }

  revokePlatformRole(
    principalId: string,
    role: PlatformRole,
  ): Promise<PlatformRoleRevokeResponse> {
    return this.json(`/v1/operator/platform-roles/${principalId}/${role}`, {
      method: "DELETE",
    });
  }

  restoreOperatorPersonalVault(
    vaultId: string,
  ): Promise<PersonalVaultRestoreResponse> {
    return this.json(`/v1/operator/personal-vaults/${vaultId}/restore`, {
      method: "POST",
    });
  }

  searchRebuildStatus(): Promise<SearchRebuildStatusResponse> {
    return this.json("/v1/operator/search-rebuild");
  }

  mutateSearchRebuild(
    input: SearchRebuildRequest,
  ): Promise<SearchRebuildStatusResponse> {
    return this.json("/v1/operator/search-rebuild", {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  createConnection(
    input: ConnectionCreateRequest,
  ): Promise<ConnectionCreateIssue> {
    return this.json("/v1/connections", {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  issueConnectionPairingCode(
    connectionId: string,
  ): Promise<ConnectionPairingIssue> {
    return this.json(`/v1/connections/${connectionId}/pairing-codes`, {
      method: "POST",
    });
  }

  rotateConnection(connectionId: string): Promise<ConnectionCredentialIssue> {
    return this.json(`/v1/connections/${connectionId}/rotate`, {
      method: "POST",
    });
  }

  replaceConnectionCapabilities(
    connectionId: string,
    capabilities: ConnectionCapability[],
  ): Promise<ConnectionInventory> {
    return this.json(`/v1/connections/${connectionId}/capabilities`, {
      method: "PUT",
      body: JSON.stringify({ capabilities }),
    });
  }

  renameConnection(
    connectionId: string,
    name: string,
  ): Promise<ConnectionInventory> {
    return this.json(`/v1/connections/${connectionId}`, {
      method: "PATCH",
      body: JSON.stringify({ name }),
    });
  }

  revokeConnection(connectionId: string): Promise<ConnectionRevokeResponse> {
    return this.json(`/v1/connections/${connectionId}/revoke`, {
      method: "POST",
    });
  }

  revokeConnectionCredential(
    connectionId: string,
    credentialId: string,
  ): Promise<CredentialRevokeResponse> {
    return this.json(
      `/v1/connections/${connectionId}/credentials/${credentialId}/revoke`,
      { method: "POST" },
    );
  }

  ingestComposeSettings(): Promise<IngestComposeSettings> {
    return this.json("/v1/ingest/compose-settings");
  }

  updateIngestComposeSettings(
    input: IngestComposeSettingsUpdateRequest,
  ): Promise<IngestComposeSettings> {
    return this.json("/v1/operator/ingest-compose-settings", {
      method: "PUT",
      body: JSON.stringify(input),
    });
  }

  ingestExtractUrl(
    vaultId: string,
    url: string,
  ): Promise<IngestExtractResponse> {
    return this.json(`/v1/vaults/${vaultId}/ingest/extract`, {
      method: "POST",
      body: JSON.stringify({ url }),
    });
  }

  ingestExtractFile(
    vaultId: string,
    file: File,
  ): Promise<IngestExtractResponse> {
    const body = new FormData();
    body.set("file", file, file.name);
    return this.json(`/v1/vaults/${vaultId}/ingest/extract`, {
      method: "POST",
      body,
    });
  }

  ingestComposeText(
    vaultId: string,
    input: IngestComposeTextRequest,
  ): Promise<IngestComposeResponse> {
    return this.json(`/v1/vaults/${vaultId}/ingest/compose`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  ingestComposeImage(
    vaultId: string,
    input: {
      readonly skillEntryId: string;
      readonly model: string;
      readonly file: File;
      readonly title?: string;
    },
  ): Promise<IngestComposeResponse> {
    const body = new FormData();
    body.set("skill_entry_id", input.skillEntryId);
    body.set("model", input.model);
    body.set("file", input.file, input.file.name);
    if (input.title !== undefined) body.set("title", input.title);
    return this.json(`/v1/vaults/${vaultId}/ingest/compose`, {
      method: "POST",
      body,
    });
  }

  listSkills(organizationId: string): Promise<{ skills: AgentSkillSummary[] }> {
    return this.json(`/v1/organizations/${organizationId}/skills`);
  }

  createOrganizationSkill(
    organizationId: string,
    input: AgentSkillCreateRequest,
  ): Promise<AgentSkillSummary> {
    return this.json(`/v1/organizations/${organizationId}/skills`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  importOrganizationSkill(
    organizationId: string,
    input: AgentSkillImportRequest,
  ): Promise<AgentSkillSummary> {
    return this.json(`/v1/organizations/${organizationId}/skills/import`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  importSkillFromUrl(
    organizationId: string,
    input: AgentSkillImportUrlRequest,
  ): Promise<AgentSkillSummary> {
    return this.json(`/v1/organizations/${organizationId}/skills/import-url`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  getSkill(
    skillId: string,
    organizationId?: string,
  ): Promise<AgentSkillDetail> {
    const query =
      organizationId === undefined
        ? ""
        : `?${new URLSearchParams({ organization_id: organizationId })}`;
    return this.json(`/v1/skills/${skillId}${query}`);
  }

  updateSkill(
    skillId: string,
    input: AgentSkillUpdateRequest,
  ): Promise<AgentSkillSummary> {
    return this.json(`/v1/skills/${skillId}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  }

  archiveSkill(skillId: string): Promise<AgentSkillSummary> {
    return this.json(`/v1/skills/${skillId}`, { method: "DELETE" });
  }

  skillFileBody(
    skillId: string,
    entryId: string,
    organizationId?: string,
  ): Promise<AgentSkillFileBody> {
    const query =
      organizationId === undefined
        ? ""
        : `?${new URLSearchParams({ organization_id: organizationId })}`;
    return this.json(`/v1/skills/${skillId}/files/${entryId}${query}`);
  }

  createSkillEntry(
    skillId: string,
    input: AgentSkillEntryCreateRequest,
  ): Promise<AgentSkillEntry> {
    return this.json(`/v1/skills/${skillId}/entries`, {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  updateSkillEntry(
    skillId: string,
    entryId: string,
    input: AgentSkillEntryUpdateRequest,
  ): Promise<AgentSkillEntry> {
    return this.json(`/v1/skills/${skillId}/entries/${entryId}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    });
  }

  deleteSkillEntry(skillId: string, entryId: string): Promise<void> {
    return this.json(`/v1/skills/${skillId}/entries/${entryId}`, {
      method: "DELETE",
    });
  }

  copySkill(
    organizationId: string,
    skillId: string,
  ): Promise<AgentSkillSummary> {
    return this.json(
      `/v1/organizations/${organizationId}/skills/${skillId}/copy`,
      { method: "POST" },
    );
  }

  promoteSkill(skillId: string): Promise<AgentSkillSummary> {
    return this.json(`/v1/skills/${skillId}/promote`, { method: "POST" });
  }

  async exportSkill(skillId: string, organizationId?: string): Promise<Blob> {
    const query =
      organizationId === undefined
        ? ""
        : `?${new URLSearchParams({ organization_id: organizationId })}`;
    const response = await this.request(
      `/v1/skills/${skillId}/export${query}`,
      {
        headers: { accept: "application/zip" },
      },
    );
    return response.blob();
  }

  async exportSkillsByTag(organizationId: string, tag: string): Promise<Blob> {
    const query = new URLSearchParams({ tag });
    const response = await this.request(
      `/v1/organizations/${organizationId}/skills/export?${query}`,
      { headers: { accept: "application/zip" } },
    );
    return response.blob();
  }

  listPlatformSkills(): Promise<{ skills: AgentSkillSummary[] }> {
    return this.json("/v1/operator/skills");
  }

  createPlatformSkill(
    input: AgentSkillCreateRequest,
  ): Promise<AgentSkillSummary> {
    return this.json("/v1/operator/skills", {
      method: "POST",
      body: JSON.stringify(input),
    });
  }

  listSkillGrants(skillId: string): Promise<{ grants: AgentSkillGrant[] }> {
    return this.json(`/v1/operator/skills/${skillId}/grants`);
  }

  grantPlatformSkill(
    skillId: string,
    organizationId: string,
  ): Promise<AgentSkillGrant> {
    return this.json(`/v1/operator/skills/${skillId}/grants`, {
      method: "POST",
      body: JSON.stringify({ organization_id: organizationId }),
    });
  }

  revokePlatformSkillGrant(
    skillId: string,
    organizationId: string,
  ): Promise<void> {
    return this.json(
      `/v1/operator/skills/${skillId}/grants/${organizationId}`,
      { method: "DELETE" },
    );
  }

  setSkillLibrary(
    organizationId: string,
    skillLibrary: boolean,
  ): Promise<{ organization_id: string; skill_library: boolean }> {
    return this.json(
      `/v1/operator/organizations/${organizationId}/skill-library`,
      {
        method: "PATCH",
        body: JSON.stringify({ skill_library: skillLibrary }),
      },
    );
  }
}
